import { apiClient } from './apiClient';

/**
 * Resolves R2 keys into presigned GET URLs for rendering media in the thread.
 *
 * Messages carry only `media.r2Key` — the signed URL is deliberately never
 * persisted, since it expires. This service signs keys on demand, caches them
 * in memory, and coalesces concurrent requests so a thread rendering twenty
 * images issues one HTTP call rather than twenty.
 */

/** Refresh this long before actual expiry, so a URL never expires mid-render. */
const EXPIRY_MARGIN_MS = 5 * 60 * 1000;

/** How long to batch key requests before issuing the call. */
const BATCH_WINDOW_MS = 50;

interface CacheEntry {
  url: string;
  expiresAt: number;
}

class MediaService {
  private cache = new Map<string, CacheEntry>();

  /** Keys waiting to go out in the next batch, with their waiting resolvers. */
  private pending = new Map<string, Array<(url: string | null) => void>>();
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  private cached(key: string): string | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    if (entry.expiresAt - EXPIRY_MARGIN_MS <= Date.now()) {
      this.cache.delete(key);
      return null;
    }
    return entry.url;
  }

  /**
   * Signed URL for one key. Concurrent calls within the batch window are
   * merged into a single request.
   */
  async resolve(key: string): Promise<string | null> {
    const hit = this.cached(key);
    if (hit) return hit;

    return new Promise((resolve) => {
      const waiters = this.pending.get(key);
      if (waiters) {
        waiters.push(resolve);
      } else {
        this.pending.set(key, [resolve]);
      }
      this.scheduleFlush();
    });
  }

  private scheduleFlush(): void {
    if (this.flushTimer !== null) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flush().catch(() => {});
    }, BATCH_WINDOW_MS);
  }

  private async flush(): Promise<void> {
    if (this.pending.size === 0) return;

    // Take the current batch and clear it, so keys requested while this call is
    // in flight queue into the next batch instead of being dropped.
    const batch = this.pending;
    this.pending = new Map();
    const keys = Array.from(batch.keys());

    try {
      const response = await apiClient.post<{
        urls: Record<string, string>;
        expiresIn: number;
      }>('/media/download-urls', { keys });

      const expiresAt = Date.now() + (response.expiresIn || 3600) * 1000;

      batch.forEach((waiters, key) => {
        const url = response.urls[key] ?? null;
        if (url) this.cache.set(key, { url, expiresAt });
        waiters.forEach((resolve) => resolve(url));
      });
    } catch (error) {
      console.error('❌ Failed to resolve media URLs:', error);
      batch.forEach((waiters) => waiters.forEach((resolve) => resolve(null)));
    }
  }

  /** Drops a cached URL so the next resolve re-signs it (used after a 403). */
  invalidate(key: string): void {
    this.cache.delete(key);
  }
}

export const mediaService = new MediaService();
export default mediaService;
